import { i18n } from '../i18n.js';
import { MoonfallGuest } from '../guest-session.js';
import { AudioSettings } from './audio-settings.js';

/**
 * chrome-menu.js
 * ---------------------------------------------------------------------------
 * ONE control in the top-right corner, on every surface that has chrome at all.
 *
 * It replaces a rail that had grown to four persistent buttons — Feedback,
 * Language, Audio and Full screen — plus the Foundry's own Lobby tab. The
 * reason is attention, not tidiness: every button that is always on screen
 * competes with the calls to action that actually matter (the store, invites,
 * Discord, and the game itself). Chrome should cost one slot.
 *
 * ═══ THIS MODULE IS PRESENTATION ONLY ═══
 *
 * It does not own audio, language or feedback state and must never appear to.
 * It ADOPTS the existing control elements — `#language-selector` built by
 * i18n.js, `#feedback-link` from index.html, `#fullscreen-toggle` built by
 * fullscreen-toggle.js — by moving those exact nodes into the panel. Same
 * elements, same listeners, same engines. Collapsing four buttons into one
 * therefore cannot reset a language, restart a soundtrack, or put a transition
 * on the music as the player crosses screens; there is no code here that could,
 * because nothing is re-instantiated and no engine is called.
 *
 * The panel body itself belongs to audio-settings.js (which builds the shell
 * and the two channel rows); this module adds the sections around them.
 *
 * ═══ CONTEXT ═══
 *
 * The menu shows only what the current surface can actually do, so it never
 * offers a dead option:
 *
 *   lobby      Audio · Display · Language · Feedback
 *   battle     Audio · Display · Match  — a match has no lobby chrome to reach,
 *                                         and Feedback mid-duel is noise; Match
 *                                         holds the one row that leaves it
 *   foundry    Audio · Display · Language · Feedback — the same menu as the
 *                                         lobby, SEATED IN THE TAB BAR where
 *                                         its own language select used to be
 *                                         (the owner, 2026-09-21: "cleaner if
 *                                         we replaced the language element in
 *                                         moon foundry with a menu button ...
 *                                         the same options that it also has in
 *                                         the lobby"), between the Stardust
 *                                         pill and the ×
 *   collection  (absent — the surface has its own ✕)
 *   store       (absent — a full-screen overlay with its own ✕)
 *
 * Absence is by CSS on the root's `data-menu-context`, never by deleting a
 * control: a readiness/context gate may hide chrome, but it must not destroy a
 * node another module still holds a reference to.
 */

const ROOT_ID = 'audio-volume-control';
const PANEL_ID = 'audio-settings-panel';

/* The DOM id is historical — this element began life as the volume puck and is
   referenced by that name from five stylesheets, twelve specs and a docs page.
   Renaming it is pure churn with a real chance of a silently non-matching CSS
   selector, so the id stays and the CONCEPT is named here instead. */

let ChromeMenu = {};

ChromeMenu = (function () {
    let root = null;
    let panel = null;
    let sections = null;
    let built = false;

    function t(key, fallback) {
        const value = i18n?.t?.(key);
        return (typeof value === 'string' && value && value !== key) ? value : fallback;
    }

    /* ── context ─────────────────────────────────────────────────────────── */

    /**
     * Is ANY element matching this selector actually painted?
     *
     * `querySelectorAll`, not `querySelector`. The single-element version reads
     * only the FIRST match in document order, so a hidden dialog earlier in the
     * DOM than an open one answers "nothing is open" — and the menu would then
     * open Escape-first on top of a modal the player is looking at. Cheap to get
     * right, silent and intermittent to get wrong.
     */
    /**
     * Is ANY element matching this selector actually on screen?
     *
     * Two traps, both of which have already bitten this function:
     *
     * 1. `querySelectorAll`, not `querySelector`. The single-element version
     *    reads only the FIRST match in document order, so a hidden dialog
     *    earlier in the DOM than an open one answers "nothing is open" — and
     *    the menu would then open on top of a modal the player is looking at.
     *
     * 2. AN ELEMENT'S OWN `display` SAYS NOTHING ABOUT ITS ANCESTORS.
     *    `.collection-container` lives inside `#collection-modal`, and while
     *    that modal is `display: none` the container still computes
     *    `display: block`. Reading only the child reported the Collection as
     *    permanently open, so the menu resolved to context `hidden` on every
     *    surface and stopped painting at all — caught by the phone spec, where
     *    the trigger reported no box in the lobby.
     *
     * The ancestor walk is used rather than `getClientRects()`/`offsetParent`
     * because jsdom performs no layout: both of those are permanently empty
     * there, which would make this branch untestable at the unit layer.
     */
    function isDisplayed(el) {
        for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
            if (node.hidden) return false;
            if (node.getAttribute('aria-hidden') === 'true') return false;
            const style = getComputedStyle(node);
            if (style.display === 'none' || style.visibility === 'hidden') return false;
        }
        return true;
    }

    function surfaceIsOpen(selector) {
        for (const el of document.querySelectorAll(selector)) {
            if (isDisplayed(el)) return true;
        }
        return false;
    }

    /**
     * Which surface owns the screen right now.
     * Read from the same body classes and open-surface markers the stylesheets
     * already use, so the menu can never disagree with the cascade about which
     * screen the player is on.
     */
    /**
     * AN ALLOW-LIST, NOT A LIST OF EXCEPTIONS.
     *
     * This used to end `return 'lobby'` — anything that was not the Foundry,
     * the Collection or the Store was treated as the lobby, including the
     * SIGN-UP SCREEN. Reported: "Many buttons should not appear on sign up
     * screen ... it should only exist in the lobby and in game."
     *
     * A default of "show it" is the wrong shape for chrome. Every screen anyone
     * adds later inherits the menu unless they remember to deny it here, and
     * nobody remembers — the auth view predates this module and still got it.
     * So the menu now paints only where it is explicitly wanted, and every
     * other state — signed out, loading, consent gate, orientation prompt, a
     * screen that does not exist yet — resolves to `hidden` by construction.
     */
    function context() {
        const body = document.body;
        if (!body) return 'hidden';

        // A match owns the screen outright, veil and all.
        if (body.classList.contains('game-active')) return 'battle';

        /* THE FOUNDRY CARRIES THE MENU, seated in its own tab bar (below).
           It used to be 'hidden' because the surface had a language select of
           its own; that select is gone and this is the control that replaced
           it, so the Foundry gets the lobby's options — Audio, Display,
           Language, Feedback — from the same node as every other screen. */
        if (body.classList.contains('foundry-surface-active')) return 'foundry';

        /* Surfaces that sit ON TOP of the lobby and carry their own exit. They
           are checked before the lobby test because the lobby view is still
           technically displayed underneath them. */
        if (surfaceIsOpen('#collection-modal, .collection-container')) return 'hidden';
        if (surfaceIsOpen('#store-modal, .store-modal')) return 'hidden';

        /* THE LOBBY MUST ACTUALLY BE ON SCREEN. `#auth-view` and `#lobby-view`
           are siblings and main.js swaps them, so "the lobby is showing" is a
           positive fact to check rather than the absence of everything else. */
        if (!surfaceIsOpen('#lobby-view')) return 'hidden';

        /* AND THE PLAYER MUST BE SIGNED IN. The lobby view can be present while
           the auth overlay is up during the signed-out-to-signed-in transition;
           `#user-controls` is the marker AGENTS.md already names as the proof of
           restored app auth. */
        if (surfaceIsOpen('#auth-view')) return 'hidden';
        if (!surfaceIsOpen('#user-controls')) return 'hidden';

        return 'lobby';
    }

    /**
     * WHERE THE CONTROL SITS, which is a different question from what it
     * offers. Everywhere but the Foundry it is the fixed corner button. The
     * Foundry's tab bar reserves a seat for it — `#foundry-menu-slot`, where
     * that surface's own language select used to be — and the control is
     * MOVED ONTO that seat rather than INTO it: the root stays a fixed,
     * top-level element and takes the seat's coordinates.
     *
     * Reparenting it into the bar was the obvious thing and it is wrong. The
     * bar clips its overflow (its tabs must never spill), and it is a low
     * layer of the Foundry's own stack — so the panel, which is absolutely
     * positioned inside the root, was cut to the bar's 53px and painted under
     * the canvas. Fixed and measured, the button is exactly where the bar
     * says while the panel keeps the whole screen to open into.
     */
    let seatObserver = null;

    function measureSeat(slot) {
        if (!root || !slot) return;
        const box = slot.getBoundingClientRect();
        if (!box.width || !box.height) return;
        /* LEFT, not a right inset computed from the viewport width: the seat
           is already inside the bar's own safe-area padding, so its measured
           left IS the answer, and deriving `right` from `window.innerWidth`
           would be exactly the viewport-clamped arithmetic the safe-area
           guard exists to keep out (cssBrittlenessGuards). */
        root.style.top = `${Math.round(box.top)}px`;
        root.style.left = `${Math.round(box.left)}px`;
        root.style.right = 'auto';
        root.style.width = `${Math.round(box.width)}px`;
        root.style.height = `${Math.round(box.height)}px`;
        /* THE MARK THAT THIS GEOMETRY IS OURS. audio-settings.js re-solves the
           same root's corner on every translation pass and resize, and that
           solve clears inline coordinates it assumes it wrote; it leaves a
           seated root alone. Without the mark, a language change made from
           inside the Foundry dropped the menu under the surface's ×. */
        root.dataset.menuSeat = 'foundry';
    }

    function clearSeat() {
        if (!root) return;
        for (const property of ['top', 'left', 'right', 'width', 'height']) root.style.removeProperty(property);
        delete root.dataset.menuSeat;
        if (seatObserver) {
            seatObserver.disconnect();
            seatObserver = null;
        }
    }

    function seat(value) {
        if (!root) return;
        const slot = value === 'foundry' ? document.getElementById('foundry-menu-slot') : null;
        if (!slot) {
            if (root.style.top || seatObserver) clearSeat();
            return;
        }
        measureSeat(slot);
        /* The bar is still settling on the frame it is mounted — the compact
           layout moves the search field into it and the tab fit re-measures
           the labels — and a button that takes its seat a beat later is a
           button the player sees jump out of the corner. Two more frames
           cost nothing and cover that settle. */
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => {
                if (root?.dataset.menuContext === 'foundry') measureSeat(slot);
                requestAnimationFrame(() => {
                    if (root?.dataset.menuContext === 'foundry') measureSeat(slot);
                });
            });
        }
        if (seatObserver) return;
        /* The bar can be measured before it has been laid out; the observer
           below reports the first real box as well as every later one. */
        if (typeof ResizeObserver === 'function') {
            /* The bar re-lays itself out on a tab switch, a viewport change and
               every Stardust figure that changes the pill's width; the seat
               reports each one. */
            seatObserver = new ResizeObserver(() => measureSeat(slot));
            seatObserver.observe(slot);
            seatObserver.observe(document.documentElement);
        }
    }

    /** Apply the current context. Idempotent; safe to call on every mutation. */
    function applyContext() {
        if (!root) return;
        const value = context();
        /* The seat is re-checked even when the context has not changed: the
           Foundry's bar is built when that surface opens, so the slot can
           appear a frame after the context does. */
        seat(value);
        if (root.dataset.menuContext === value) return;
        root.dataset.menuContext = value;
        // Never leave an open panel behind on a surface that has no menu.
        if (value === 'hidden') close();
    }

    /* ── section scaffolding ─────────────────────────────────────────────── */

    function makeSection(name, labelKey, labelFallback) {
        const section = document.createElement('div');
        section.className = 'chrome-menu-section';
        section.dataset.section = name;

        const heading = document.createElement('h3');
        heading.className = 'chrome-menu-heading';
        heading.dataset.i18n = labelKey;
        heading.textContent = t(labelKey, labelFallback);
        section.appendChild(heading);

        const body = document.createElement('div');
        body.className = 'chrome-menu-section-body';
        section.appendChild(body);

        return { section, body, heading };
    }

    /**
     * Build the sections and fold the audio rows that audio-settings.js already
     * created into the Audio one. The rows are MOVED, not rebuilt — they carry
     * live listeners bound to the soundtrack and SFX engines.
     */
    function build() {
        // No frame guard: the same-origin Foundry frame this once deferred to
        // is retired, and a third-party embed (itch.io) has no parent chrome.
        if (built) return;
        root = document.getElementById(ROOT_ID);
        panel = document.getElementById(PANEL_ID);
        if (!root || !panel) return;

        built = true;
        root.classList.add('chrome-menu');
        panel.classList.add('chrome-menu-panel');

        const audio = makeSection('audio', 'menu.audio', 'Audio');
        const display = makeSection('display', 'menu.display', 'Display');
        /* Language and Feedback get a group each rather than sharing a
           "General" bucket, and Language comes first — both owner calls. They
           are unrelated errands: one is a preference you set once, the other is
           an action that opens a form. A shared heading made the panel read as
           a list of leftovers. */
        const language = makeSection('language', 'menu.language', 'Language');
        const feedback = makeSection('feedback', 'menu.feedback', 'Feedback');
        /* Leaving a match is the one destructive thing in here, so it sits
           last, on its own, where a thumb does not pass over it on the way to
           anything else. The section is named for what it acts on — the match
           ("Match" over an "Exit Match" row), not for where the row leads; the
           `lobby` DOM name stays because five stylesheets and the specs key on
           it. */
        const lobby = makeSection('lobby', 'menu.match', 'Match');

        // Adopt the two channel rows built by audio-settings.js.
        for (const row of Array.from(panel.querySelectorAll('.audio-channel-row'))) {
            audio.body.appendChild(row);
        }

        panel.appendChild(audio.section);
        panel.appendChild(display.section);
        panel.appendChild(language.section);
        panel.appendChild(feedback.section);
        panel.appendChild(lobby.section);

        sections = { audio, display, language, feedback, lobby };

        adoptFullscreen();
        adoptLanguage();
        adoptFeedback();
        adoptLobbyExit();
        adoptDebugEndMatch();
        applyContext();
        installWatchers();
    }

    /* ── adoption ────────────────────────────────────────────────────────── */

    /**
     * The full-screen trigger. fullscreen-toggle.js still owns the button
     * entirely — capability detection, the click handler, the enter/exit label
     * read back from the document after every attempt. All that changes is
     * where it is painted, so a rejected request still relabels itself
     * correctly and iPhone still never gets a dead control.
     */
    function adoptFullscreen() {
        const button = document.getElementById('fullscreen-toggle');
        if (!button || !sections) return false;
        if (button.parentElement === sections.display.body) return true;
        button.classList.add('chrome-menu-row');
        sections.display.body.appendChild(button);
        return true;
    }

    /**
     * The language selector, built by i18n.js into `#lobby-language-cluster`.
     * Moved whole: its button, its listbox and every option keep the listeners
     * i18n bound to them, so the selected language is untouched by this move —
     * the element is not recreated and `setLanguage` is never called.
     */
    function adoptLanguage() {
        const selector = document.getElementById('language-selector');
        if (!selector || !sections) return false;
        if (selector.parentElement === sections.language.body) return true;
        sections.language.body.appendChild(selector);
        return true;
    }

    /** The Feedback button from index.html, with its bound click handler. */
    function adoptFeedback() {
        const link = document.getElementById('feedback-link');
        if (!link || !sections) return false;
        if (link.parentElement === sections.feedback.body) return true;
        link.classList.add('chrome-menu-row');
        sections.feedback.body.appendChild(link);
        return true;
    }


    /**
     * THE IN-MATCH EXIT — folded in, but ONLY when it is the exit.
     *
     * `#skip-tutorial-btn` is one element wearing two jobs: lobby.js rewrites it
     * between "Exit Match" and "Skip Tutorial" depending on
     * `MoonfallGuest.isActive()`. Folding it away unconditionally would bury the
     * tutorial's Skip control behind a menu during onboarding — and
     * `tutorial-cues.js` points a cue at that button by selector, so the cue
     * would ring a hidden node.
     *
     * So the guest tutorial keeps its visible Skip button and EVERY other state
     * gets Exit Match as a menu row — including a landscape phone. A gate used to
     * exempt phones here, reading a `--mf-battle-exit-standalone` flag the
     * stylesheet published because that stylesheet pinned the exit to the screen
     * corner as a ✕ for every match. Both are gone: "i sayd we should remove x
     * button. lobby function should be inside menu button ... just whatever is
     * on desktop should also be on mobile." The element is MOVED either way,
     * never rebuilt, so the confirm dialog lobby.js binds to it survives every
     * direction.
     */
    function adoptLobbyExit() {
        const exit = document.getElementById('skip-tutorial-btn');
        if (!exit || !sections) return false;

        if (MoonfallGuest?.isActive?.() === true) {
            // Hand it back to the stylesheet if a previous pass adopted it.
            if (exit.parentElement === sections.lobby.body) {
                exit.classList.remove('chrome-menu-row');
                document.body.appendChild(exit);
            }
            return true;
        }

        if (exit.parentElement === sections.lobby.body) return true;
        exit.classList.add('chrome-menu-row');
        /* The battle solver may have left an inline seat on it. Clear it here
           rather than in audio-settings, so the element cannot arrive in the
           panel still wearing `position: fixed` coordinates. */
        exit.style.left = '';
        exit.style.top = '';
        exit.style.right = '';
        exit.style.bottom = '';
        exit.style.transform = '';
        delete exit.dataset.layout;
        sections.lobby.body.appendChild(exit);
        return true;
    }

    /**
     * THE DEV CONCEDE BELONGS IN THE MENU, NOT IN THE COMMAND RAIL.
     *
     * `#debug-end-match-btn` (DEBUG_MODE only, `display: none` otherwise) was
     * `position: fixed` at `top: calc(50% + 7vmin)` — inside the band the exit
     * control has owned on every layout since the rail was unified. Two live
     * overlaps came out of that:
     *
     *   - on desktop it pushed the exit out of its declared home and into the
     *     countdown's band, which is the defect the owner reported ("skip
     *     tutorial button overlaps mulligan timer on my desktop monitor");
     *   - on a landscape phone the exit is CSS-owned and cannot move, so the
     *     two simply printed on top of each other — measured 248px² at
     *     915x336, 111px² at 844x288, in Chrome and Firefox alike.
     *
     * There is no free band left in that column on a phone page box: the
     * summoner cluster starts immediately below. So the concede leaves the rail
     * the same way the match exit did — it is a rare command, and rare commands
     * live in the menu. A DEBUG build now lays out exactly like the shipping
     * one, which is the property a dev build should have.
     *
     * MOVED, never rebuilt: main.js binds the click handler to this element, so
     * re-parenting keeps it.
     *
     * `#debug-win-match-btn` is the concede's mirror (the requester WINS, for
     * reaching the victory result screen) and rides along under exactly the
     * same rules, seated directly after the concede so the two dev rows read
     * as one pair. `#debug-reveal-hand-btn` (the opponent's hand face-up, the
     * storyteller-reveal state) is the third row of the same set, and
     * `#debug-board-reveal-btn` (the AI's Characters on its board plus one
     * revealed hand card, in one press) the fourth.
     *
     * GATED ON THE FLAG, NOT ON `display`. A hidden row is not free here:
     * chrome-menu.css hides any section whose body matches `:empty`, and that
     * rule is the only thing keeping a "LOBBY" heading from floating over
     * nothing during the guest tutorial — where `adoptLobbyExit()` deliberately
     * hands the exit BACK to the page. Parking a `display: none` button in that
     * body would defeat `:empty` and bring the empty-heading defect back for
     * every production player. `config.js` publishes `CLIENT_DEBUG_MODE`
     * synchronously during parse, so it is already settled by the time this
     * first runs, and `adoptPending()` re-runs on the i18n events anyway.
     */
    function adoptDebugEndMatch() {
        if (window.CLIENT_DEBUG_MODE !== true) return true;
        if (!sections) return false;
        let landed = true;
        for (const id of ['debug-end-match-btn', 'debug-win-match-btn', 'debug-reveal-hand-btn', 'debug-board-reveal-btn']) {
            const control = document.getElementById(id);
            if (!control) { landed = false; continue; }
            if (control.parentElement === sections.lobby.body) continue;
            control.classList.add('chrome-menu-row');
            sections.lobby.body.appendChild(control);
        }
        return landed;
    }

    /**
     * Controls that other modules create asynchronously arrive after the first
     * build. Re-attempt adoption until each has landed, then stop watching —
     * cheaper and far less fragile than a document-wide subtree observer.
     */
    function adoptPending() {
        /* EVERY adopter runs, THEN they are combined. `a() && b() && c()`
           short-circuits, and on an iPhone the first one is always false —
           iOS Safari exposes no Fullscreen API, so fullscreen-toggle.js never
           creates `#fullscreen-toggle` — which meant Language and Feedback were
           never adopted at all on the platform the owner's reports come from.
           The language button then stayed loose in the top-right corner, which
           is the exact defect folding the four corner controls into one menu
           was meant to remove. Measured on WebKit/iPhone 13 at 667x275: the
           selector sat in `#lobby-language-cluster` forever, and calling
           `adoptPending()` by hand returned false without moving anything. */
        const fullscreen = adoptFullscreen();
        const language = adoptLanguage();
        const feedback = adoptFeedback();
        const concede = adoptDebugEndMatch();
        const done = fullscreen && language && feedback && concede;
        // Always re-run: this one flips with the guest-tutorial state, not just
        // with whether the element has appeared yet.
        adoptLobbyExit();
        return done;
    }

    /* ── open / close ────────────────────────────────────────────────────── */

    // Open/close live in audio-settings.js, which owns the panel's focus
    // management and outside-press dismissal. This module only forwards, so
    // there is exactly one implementation of "is the panel open".
    //
    // Imported, never read off `window`: a module reached through a global is
    // a module that silently becomes undefined the moment the bundler stops
    // publishing it, and `if (window.X)` then disables the feature instead of
    // failing. An import is a build-time error if it breaks.
    function isOpen() { return !!AudioSettings?.isOpen?.(); }
    function open() { AudioSettings?.open?.(); }
    function close() { AudioSettings?.close?.(); }
    function toggle() { return isOpen() ? close() : open(); }

    /* ── keyboard ────────────────────────────────────────────────────────── */

    /**
     * Esc opens the menu, and M does too.
     *
     * Esc alone is the genre convention and it is what a player reaches for,
     * but it cannot be the whole answer: while the document is in full screen
     * the BROWSER takes Escape to leave full screen, and a page cannot
     * preventDefault that or reword the "Press Esc to exit full screen" bubble,
     * which is browser UI drawn outside the document. In practice that reads
     * fine — Esc drops full screen, Esc again opens the menu — but it leaves no
     * keyboard route to the menu without first leaving full screen, which is
     * exactly when a player is most likely to want it. Hence the second key.
     *
     * (The one API that would keep Escape in full screen, `navigator.keyboard
     * .lock`, is Chromium-only and converts the exit gesture to press-and-hold.
     * Taking over a browser exit affordance in one engine only is a product
     * decision, not a styling one, so it is deliberately not done here.)
     */
    /**
     * A field that a letter key would go INTO.
     *
     * Deliberately not "any `<input>`". Opening the panel focuses the music
     * slider, which is `<input type="range">` — so treating every input as a
     * typing target made `M` unable to CLOSE the menu it had just opened, while
     * Escape still worked because audio-settings.js handles that one itself.
     * A range, checkbox, radio or button input does not consume letter keys, so
     * only the text-entry types (and contenteditable) suppress the shortcut.
     */
    const TEXT_ENTRY_TYPES = new Set([
        'text', 'search', 'email', 'url', 'tel', 'password', 'number',
        'date', 'datetime-local', 'month', 'week', 'time',
    ]);

    function isTypingTarget(target) {
        if (!target || target.nodeType !== 1) return false;
        // Property AND attribute: `isContentEditable` is the right API in a
        // browser, but it is unimplemented in jsdom, so the unit layer could
        // not cover this branch at all without the attribute check beside it.
        if (target.isContentEditable) return true;
        const editable = target.getAttribute?.('contenteditable');
        if (editable === '' || editable === 'true' || editable === 'plaintext-only') return true;
        const tag = target.tagName;
        if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
        if (tag !== 'INPUT') return false;
        // A missing/unknown `type` is a text field per the HTML spec.
        const type = (target.getAttribute('type') || 'text').toLowerCase();
        return TEXT_ENTRY_TYPES.has(type);
    }

    /**
     * Anything modal that is already on screen owns Escape. The menu is the
     * lowest-priority Escape handler in the app: it must never steal the key
     * from a dialog the player is actually looking at, and it must never open
     * on top of one.
     */
    function anotherOverlayOwnsEscape() {
        return surfaceIsOpen('[role="dialog"]:not(#' + PANEL_ID + ')')
            || surfaceIsOpen('[aria-modal="true"]')
            || surfaceIsOpen('#mulligan-overlay')
            || surfaceIsOpen('#rotate-overlay');
    }

    function onKeydown(event) {
        if (event.defaultPrevented || event.repeat) return;
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (isTypingTarget(event.target)) return;

        const key = event.key;
        const isEscape = key === 'Escape';
        const isMenuKey = key === 'm' || key === 'M';
        if (!isEscape && !isMenuKey) return;

        // Closing is always allowed — including from inside the panel, where
        // audio-settings' own Escape handler already runs first and will have
        // consumed the event.
        if (isOpen()) {
            event.preventDefault();
            close();
            return;
        }
        if (root?.dataset.menuContext === 'hidden') return;
        if (anotherOverlayOwnsEscape()) return;

        event.preventDefault();
        open();
    }

    /* ── watchers ────────────────────────────────────────────────────────── */

    /* Surfaces that open and close WITHOUT touching `document.body`.

       The Collection modal is appended to `body` once and then shown and
       hidden in place, so the body observer sees it arrive and never sees it
       leave: the menu stayed hidden after the player closed the Collection and
       returned to the lobby. Each host is adopted the first time it is found
       and watched for the attributes its own open/close actually flips. */
    const SURFACE_HOSTS = [
        '#collection-modal', '#store-modal', '#foundry-surface-host',
        /* The auth/lobby swap is a `style` change on these two, not a body
           class, so without watching them the menu would keep whatever context
           it resolved to when the page loaded — which on a cold signed-out load
           is exactly the sign-up screen. */
        '#auth-view', '#lobby-view', '#user-controls',
    ];

    /* WHERE THE ADOPTEES ARE BORN.

       i18n.js appends `#language-selector` into `#lobby-language-cluster`, and
       that is a mutation of the CLUSTER, not of `document.body` — so the body
       observer below never saw it. If i18n finished after this module's first
       build, the selector simply stayed outside the panel until some unrelated
       body mutation happened to re-trigger adoption. Measured on three tablet
       page boxes (1366x974, 1376x982, 1024x768), where it painted as a loose
       pill in the corner beside the menu. */
    const ADOPTEE_HOSTS = ['#lobby-language-cluster'];
    const watchedHosts = new WeakSet();

    function watchSurfaceHosts(observer) {
        for (const selector of ADOPTEE_HOSTS) {
            const host = document.querySelector(selector);
            if (!host || watchedHosts.has(host)) continue;
            watchedHosts.add(host);
            observer.observe(host, { childList: true });
        }
        for (const selector of SURFACE_HOSTS) {
            const host = document.querySelector(selector);
            if (!host || watchedHosts.has(host)) continue;
            watchedHosts.add(host);
            observer.observe(host, {
                attributes: true,
                attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
            });
            /* THE FOUNDRY'S SEAT IS BORN INSIDE ITS HOST. The body class that
               makes the context 'foundry' is set before the surface template
               is cloned in, so the first `applyContext` finds no seat to
               stand on and the context never changes again to trigger a
               second one. Watching the host's children is what lets the
               control take its seat the moment the bar exists. */
            if (selector === '#foundry-surface-host') {
                observer.observe(host, { childList: true, subtree: true });
            }
        }
    }

    let watchersInstalled = false;
    function installWatchers() {
        if (watchersInstalled) return;
        watchersInstalled = true;

        // Context follows the same body classes and surface markers the
        // stylesheets read, so one observer covers every screen change.
        if (typeof MutationObserver === 'function') {
            const observer = new MutationObserver(() => {
                try {
                    applyContext();
                    adoptPending();
                    watchSurfaceHosts(observer);
                } catch (_) { /* unloading */ }
            });
            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ['class'],
                childList: true,
            });
            watchSurfaceHosts(observer);
        }

        document.addEventListener('keydown', onKeydown);
        /* Adopt as well as relabel: i18n builds the language selector during
           its own startup, which can land after this module's first build. */
        for (const event of ['moonfall:i18nready', 'moonfall:languagechange']) {
            window.addEventListener(event, () => { relabel(); adoptPending(); });
        }
        document.addEventListener('moonfall:i18n-translated', adoptPending);
    }

    function relabel() {
        if (!sections) return;
        for (const [key, fallback, section] of [
            ['menu.audio', 'Audio', sections.audio],
            ['menu.display', 'Display', sections.display],
            ['menu.language', 'Language', sections.language],
            ['menu.feedback', 'Feedback', sections.feedback],
            ['menu.match', 'Match', sections.lobby],
        ]) {
            section.heading.textContent = t(key, fallback);
        }
    }

    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', build, { once: true });
        } else {
            build();
        }
        // audio-settings.js and fullscreen-toggle.js may finish after this
        // module first runs; a microtask-later retry costs nothing and removes
        // the ordering dependency between the three.
        setTimeout(build, 0);
    }

    return {
        build,
        open,
        close,
        toggle,
        isOpen,
        context,
        applyContext,
        adoptPending,
        _internals: { ROOT_ID, PANEL_ID, isTypingTarget, anotherOverlayOwnsEscape, onKeydown },
    };
})();

export { ChromeMenu };
